import { useEffect, useState } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useSelector } from 'react-redux';
import {
  Grid, Typography, CircularProgress, Card, Select, MenuItem,
  Button, Divider, Chip, Box, Alert, Paper, Avatar,
  List, ListItem, ListItemText, LinearProgress, TextField,
  Dialog, DialogTitle, DialogContent, DialogActions, IconButton, Tooltip,
} from '@mui/material';
import {
  Email as EmailIcon, Phone as PhoneIcon, LocationOn as LocationIcon,
  CalendarToday as CalendarIcon, Payment as PaymentIcon,
  ShoppingCart as ShoppingCartIcon, LocalOffer as LocalOfferIcon,
  Receipt as ReceiptIcon, CheckCircle as CheckCircleIcon,
  Autorenew as AutorenewIcon, Public as CountryIcon,
  ConfirmationNumber as ConfirmationNumberIcon,
  PlayArrow as PlayIcon, Pause as PauseIcon, Refresh as RefreshIcon,
  AttachMoney as RefundIcon, ArrowForward as ArrowForwardIcon,
  EventBusy as EventBusyIcon,
  Check as CheckIcon,
  Download as DownloadIcon,
  CloudUpload as CloudUploadIcon,
} from '@mui/icons-material';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDBadge from 'components/MDBadge';
import MDButton from 'components/MDButton';
import { ENDPOINT } from 'constants';
import { Loading, Notify, Confirm } from 'notiflix';
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import DashboardNavbar from 'examples/Navbars/DashboardNavbar';
import Footer from 'examples/Footer';
import { format, addMonths } from 'date-fns';
import usePermission from 'hooks/usePermission';

// ─── subscription helpers ───────────────────────────────────────────────────
const getSubscriptionIdForItem = (order, itemId) =>
  (order.subscriptions || []).find(
    (s) => s.orderValuePackItemId === itemId || s.orderItemId === itemId,
  )?.id || null;
const fmtMoney = (amount, currency = 'USD') => {
  if (amount == null) return `${currency} 0.00`;
  return `${currency} ${parseFloat(amount).toFixed(2)}`;
};

const safeDate = (d, pattern = 'MMM dd, yyyy') => {
  try { return format(new Date(d), pattern); } catch { return 'N/A'; }
};

const SUB_STATUS_COLORS = {
  active: 'success', past_due: 'warning', canceled: 'error',
  expired: 'dark', paused: 'secondary', incomplete: 'info',
  trialing: 'info', unpaid: 'error',
};

// ─── Payment-progress flow (Completed → Remaining → Next Payment) ──────────
const FlowNode = ({ shape, bg, label, value, caption }) => (
  <Box
    sx={{
      bgcolor: bg,
      color: '#fff',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      textAlign: 'center',
      borderRadius: shape === 'pill' ? 999 : '50%',
      width: shape === 'pill' ? 168 : 132,
      height: shape === 'pill' ? 104 : 132,
      flexShrink: 0,
      boxShadow: '0 6px 16px rgba(0,0,0,0.18)',
      px: 1.5,
    }}>
    <Typography variant="caption" sx={{ opacity: 0.85, textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 700, fontSize: '0.65rem' }}>
      {label}
    </Typography>
    <Typography variant="h5" fontWeight="bold" sx={{ my: 0.3, lineHeight: 1.1 }}>
      {value}
    </Typography>
    <Typography variant="caption" sx={{ opacity: 0.95, fontSize: '0.68rem' }}>
      {caption}
    </Typography>
  </Box>
);

const FlowArrow = () => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
      mx: { xs: 0, sm: 1.5 },
      my: { xs: 0.5, sm: 0 },
      transform: { xs: 'rotate(90deg)', sm: 'none' },
    }}>
    <ArrowForwardIcon sx={{ color: '#9CA3AF', fontSize: 30 }} />
  </Box>
);

const CYCLE_STATUS_STYLE = {
  completed: { color: '#16A34A', label: 'Paid' },
  current: { color: '#4F46E5', label: 'Current' },
  upcoming: { color: '#94A3B8', label: 'Upcoming' },
};

// A connected step marker — filled + checkmark once paid, a highlighted ring on the
// current cycle, an outlined circle for anything still upcoming. This is the standard
// "progress stepper" pattern (checkout/shipment tracking), just applied to billing cycles.
const CycleStepMarker = ({ cycleNum, status }) => {
  const isCompleted = status === 'completed';
  const isCurrent = status === 'current';
  return (
    <Box
      sx={{
        width: 36,
        height: 36,
        borderRadius: '50%',
        flexShrink: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontWeight: 700,
        fontSize: '0.8rem',
        bgcolor: isCompleted ? '#16A34A' : isCurrent ? '#4F46E5' : '#fff',
        color: isCompleted || isCurrent ? '#fff' : '#94A3B8',
        border: isCompleted || isCurrent ? 'none' : '2px solid #CBD5E1',
        boxShadow: isCurrent ? '0 0 0 4px rgba(79,70,229,0.18)' : 'none',
      }}>
      {isCompleted ? <CheckIcon sx={{ fontSize: 18 }} /> : cycleNum}
    </Box>
  );
};

// One step per billing cycle, connected by a line (green where already paid, gray ahead)
// — shown in a horizontally-scrolling row so a 6/7+ cycle subscription stays fully visible.
const CycleStep = ({ cycleNum, cycleDate, status }) => {
  const style = CYCLE_STATUS_STYLE[status];
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0, minWidth: 92 }}>
      <CycleStepMarker cycleNum={cycleNum} status={status} />
      <Typography variant="caption" color="text.secondary" sx={{ mt: 1, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4, fontSize: '0.62rem' }}>
        Cycle {cycleNum}
      </Typography>
      <Typography variant="body2" fontWeight="bold" sx={{ mt: 0.2 }}>
        {safeDate(cycleDate, 'MMM dd')}
      </Typography>
      <Typography variant="caption" fontWeight={600} sx={{ mt: 0.2, color: style.color }}>
        {style.label}
      </Typography>
    </Box>
  );
};

// The connecting line segment between two steps — colored in once the step to its left
// is paid, gray for anything still ahead.
const CycleConnector = ({ filled }) => (
  <Box sx={{ width: 40, flexShrink: 0, height: 3, mt: '17px', bgcolor: filled ? '#16A34A' : '#E2E8F0', borderRadius: 2 }} />
);

// ─── SubscriptionPanel ───────────────────────────────────────────────────────
// Embedded, trimmed-down version of the standalone Subscription Details page.
// Customer info and the "Related Order" card are dropped here because both
// are already shown on this same Order Details page — everything else is kept.
const SubscriptionPanel = ({ subscriptionId, authHeader }) => {
  const canUpdate = usePermission('orders', 'update');
  const [sub, setSub] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refundDialog, setRefundDialog] = useState(false);
  const [refundAmount, setRefundAmount] = useState('');
  const [refundReason, setRefundReason] = useState('requested_by_customer');

  // Every admin subscription/order endpoint lives in the dedicated admin-ops router
  // (routes/admin/subscriptionOrderAdmin.routes.js / controllers/admin/subscriptionOrderAdmin.controllers.js).
  const adminOpsUrl = `${ENDPOINT}api/v1/admin-ops/subscriptions/${subscriptionId}`;

  const fetchSub = async () => {
    try {
      const { data } = await axios.get(adminOpsUrl, authHeader);
      if (data.success) setSub(data.data);
      else Notify.failure('Failed to fetch subscription');
    } catch (err) {
      console.error('Fetch subscription error:', err);
      Notify.failure(err.response?.data?.message || 'Error fetching subscription');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchSub(); }, [subscriptionId]);

  const refreshAfter = (ms = 1500) => setTimeout(fetchSub, ms);
  const withConfirm = (title, msg, okLabel, action) => Confirm.show(title, msg, okLabel, 'No', action);

  const gatewayLabel = sub?.gateway === 'razorpay' ? 'Razorpay' : 'Stripe';

  const handleVerifyWithGateway = async () => {
    try {
      Loading.circle(`Verifying with ${gatewayLabel}...`);
      const { data } = await axios.get(`${adminOpsUrl}/verify`, authHeader);
      if (data.success) {
        Notify.success(data.data.synced ? `In sync with ${gatewayLabel}` : `Synced with ${gatewayLabel}`);
        if (!data.data.synced) fetchSub();
      }
    } catch (err) { Notify.failure(err.response?.data?.message || 'Failed to verify'); }
    finally { Loading.remove(); }
  };

  const handleCancelNow = () => withConfirm(
    'Cancel Subscription', 'Cancel immediately? The user will be notified by email.', 'Yes, Cancel',
    async () => {
      try {
        Loading.circle('Canceling...');
        await axios.patch(
          `${adminOpsUrl}/cancel`,
          {customerLabel: sub?.subscriptionNumber || ''},
          authHeader,
        );
        Notify.success('Subscription canceled');
        refreshAfter();
      } catch (err) {
        Notify.failure(err.response?.data?.message || 'Failed to cancel');
        fetchSub();
      } finally { Loading.remove(); }
    }
  );

  const handleCancelAtPeriodEnd = () => withConfirm(
    'Cancel at Period End', 'The subscription stays active until the billing period ends.', 'Yes, Schedule',
    async () => {
      try {
        Loading.circle('Scheduling...');
        await axios.patch(
          `${adminOpsUrl}/cancel-at-period-end`,
          {customerLabel: sub?.subscriptionNumber || ''},
          authHeader,
        );
        Notify.success('Cancellation scheduled');
        refreshAfter();
      } catch (err) {
        Notify.failure(err.response?.data?.message || 'Failed to schedule');
        fetchSub();
      } finally { Loading.remove(); }
    }
  );

  const handlePause = () => withConfirm('Pause Subscription', 'Pause billing until resumed?', 'Yes, Pause', async () => {
    try {
      Loading.circle('Pausing...');
      await axios.patch(
        `${adminOpsUrl}/pause`,
        {customerLabel: sub?.subscriptionNumber || ''},
        authHeader,
      );
      Notify.success('Subscription paused');
      refreshAfter();
    } catch (err) {
      Notify.failure(err.response?.data?.message || 'Failed to pause');
      fetchSub();
    } finally { Loading.remove(); }
  });

  const handleResume = () => withConfirm('Resume Subscription', 'Resume billing?', 'Yes, Resume', async () => {
    try {
      Loading.circle('Resuming...');
      await axios.patch(
        `${adminOpsUrl}/resume`,
        {customerLabel: sub?.subscriptionNumber || ''},
        authHeader,
      );
      Notify.success('Subscription resumed');
      refreshAfter();
    } catch (err) {
      Notify.failure(err.response?.data?.message || 'Failed to resume');
      fetchSub();
    } finally { Loading.remove(); }
  });

  const handleSendInvoice = () => withConfirm('Send Invoice', 'Send an invoice to the customer now?', 'Yes, Send', async () => {
    try {
      Loading.circle('Sending...');
      const { data } = await axios.post(`${adminOpsUrl}/send-invoice`, {}, authHeader);
      if (data.success) {
        Notify.success('Invoice sent');
        if (data.data.invoiceUrl) window.open(data.data.invoiceUrl, '_blank');
      }
    } catch { Notify.failure('Failed to send invoice'); }
    finally { Loading.remove(); }
  });

  const handleIssueRefund = async () => {
    try {
      Loading.circle('Processing refund...');
      const payload = {
        amount: refundAmount ? parseFloat(refundAmount) : undefined,
        reason: refundReason || 'requested_by_customer',
        cancelSubscription: true,
      };
      const { data } = await axios.post(`${adminOpsUrl}/refund`, payload, authHeader);
      if (data.success) {
        Notify.success('Refund issued and subscription canceled');
        setRefundDialog(false);
        setRefundAmount('');
        setRefundReason('requested_by_customer');
        refreshAfter(800);
      }
    } catch (err) {
      Notify.failure(err.response?.data?.message || 'Failed to issue refund');
    } finally { Loading.remove(); }
  };

  if (loading) {
    return (
      <Paper elevation={0} sx={{ p: 3, textAlign: 'center', bgcolor: '#f8f9fa', borderRadius: 2 }}>
        <CircularProgress size="2rem" />
        <Typography variant="body2" sx={{ mt: 1 }}>Loading subscription…</Typography>
      </Paper>
    );
  }

  if (!sub) {
    return <Alert severity="warning">Could not load subscription details.</Alert>;
  }

  const payment = sub.payment || {};
  const dates = sub.dates || {};
  const cycles = sub.cycles || {};
  const cancellation = sub.cancellation || {};
  const metadata = sub.metadata || {};
  const subCurrency = payment.currency || 'USD';
  const isRefunded = !!metadata.refunded;
  const statusColor = SUB_STATUS_COLORS[sub.status] || 'info';

  // One entry per billing cycle (1..total). The backend now returns real per-cycle history
  // (cycles.history) — actual payment/renewal dates where they exist, only falling back to
  // an estimated date (startDate + N months) for cycles that haven't been charged yet. If
  // that array is missing for any reason, fall back to deriving every date client-side.
  const cycleBaseDate = dates.startDate || dates.currentPeriodStart;
  const cycleNodes = cycles.history?.length > 0
    ? cycles.history.map((c) => ({
      cycleNum: c.cycleNumber,
      cycleDate: c.date,
      status: c.status === 'paid' ? 'completed' : c.status,
    }))
    : cycles.total && cycleBaseDate
      ? Array.from({ length: cycles.total }, (_, i) => {
        const cycleNum = i + 1;
        return {
          cycleNum,
          cycleDate: addMonths(new Date(cycleBaseDate), i),
          status: cycleNum < (cycles.current ?? 0) ? 'completed'
            : cycleNum === (cycles.current ?? 0) ? 'current'
              : 'upcoming',
        };
      })
      : [];

  return (
    <Box
      sx={{
        p: 2.5,
        borderRadius: 3,
        bgcolor: '#EEF2FF',
        border: '1px solid #C7D2FE',
        borderLeft: '4px solid #4F46E5',
      }}>
      {/* Panel header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AutorenewIcon sx={{ color: '#4F46E5' }} />
          <Box>
            <Typography variant="subtitle1" fontWeight="bold" color="#4F46E5" lineHeight={1.2}>
              Subscription
            </Typography>
            <Typography variant="caption" color="text.secondary">{sub.subscriptionNumber || sub.id}</Typography>
          </Box>
        </Box>
        <MDBadge badgeContent={sub.status} color={statusColor} variant="gradient" size="lg" />
      </Box>

      {/* Action bar */}
      <Paper elevation={0} sx={{ p: 2, mb: 2, bgcolor: '#fff', border: '1px solid #E0E7FF', borderRadius: 2 }}>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
          <Tooltip title={`Verify with ${gatewayLabel}`}>
            <IconButton onClick={handleVerifyWithGateway} sx={{ bgcolor: '#2196f3', color: '#fff' }} size="small">
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          {canUpdate && (
            <Tooltip title="Send Invoice">
              <span>
                <IconButton onClick={handleSendInvoice} disabled={sub.status === 'canceled'} sx={{ bgcolor: '#673ab7', color: '#fff' }} size="small">
                  <ReceiptIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          )}
          {canUpdate && (
            <Tooltip title="Issue Refund">
              <span>
                <IconButton onClick={() => setRefundDialog(true)} disabled={sub.status === 'canceled'} sx={{ bgcolor: '#ff9800', color: '#fff' }} size="small">
                  <RefundIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          )}

          {sub.status === 'paused' && canUpdate && (
            <Button size="small" variant="contained" startIcon={<PlayIcon />} onClick={handleResume}
              sx={{ bgcolor: '#16A34A', borderRadius: 1.5, fontWeight: 600, boxShadow: 'none', '&:hover': { bgcolor: '#15803D', boxShadow: 'none' } }}>
              Resume
            </Button>
          )}
        </Box>
      </Paper>

      {/* Alerts */}
      {cancellation.cancelAtPeriodEnd && sub.status !== 'canceled' && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          ⏰ This subscription will cancel on <strong>{safeDate(dates.currentPeriodEnd, 'MMMM dd, yyyy')}</strong>
        </Alert>
      )}
      {sub.status === 'canceled' && (
        <Alert severity="error" sx={{ mb: 2 }}>
          ❌ Canceled on <strong>{safeDate(dates.canceledAt || cancellation.canceledAt, 'MMMM dd, yyyy')}</strong>
          {cancellation.cancellationReason && (
            <Typography variant="body2">Reason: {cancellation.cancellationReason}</Typography>
          )}
        </Alert>
      )}
      {sub.status === 'paused' && (
        <Alert severity="info" sx={{ mb: 2 }}>⏸️ This subscription is paused. Billing has been suspended.</Alert>
      )}
      {isRefunded && (
        <Alert severity="success" sx={{ mb: 2 }}>
          ✅ Refund issued on <strong>{safeDate(metadata.refundedAt, 'MMMM dd, yyyy')}</strong>
          <Typography variant="body2">
            Amount: {fmtMoney(metadata.refundAmount, subCurrency)} — ID: {metadata.refundId}
          </Typography>
        </Alert>
      )}

      {/* Payment progress: every billing cycle, oldest to newest */}
      <Paper elevation={0} sx={{ p: 2.5, mb: 2, bgcolor: '#fff', border: '1px solid #E0E7FF', borderRadius: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 1 }}>
          <Typography variant="subtitle2" fontWeight="bold" color="#4F46E5">📊 Payment Progress</Typography>
          <Typography variant="caption" color="text.secondary" fontWeight={700}>
            {cycles.current ?? 0} of {cycles.total || '—'} cycles paid · {fmtMoney(payment.totalPaid, subCurrency)}
          </Typography>
        </Box>
        <Divider sx={{ mb: 2 }} />

        {cycleNodes.length > 0 ? (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              overflowX: 'auto',
              pb: 1,
              '&::-webkit-scrollbar': { height: 6 },
              '&::-webkit-scrollbar-thumb': { bgcolor: '#C7D2FE', borderRadius: 3 },
            }}>
            {cycleNodes.map((c, idx) => (
              <Box key={c.cycleNum} sx={{ display: 'flex', alignItems: 'flex-start' }}>
                <CycleStep {...c} />
                {idx < cycleNodes.length - 1 && <CycleConnector filled={c.status === 'completed'} />}
              </Box>
            ))}
          </Box>
        ) : (
          // Fallback when the total cycle count isn't known (e.g. open-ended flexible subs)
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: 'center', justifyContent: 'center', flexWrap: 'wrap' }}>
            <FlowNode
              shape="pill"
              bg="#4F46E5"
              label="Completed"
              value={cycles.current ?? 0}
              caption={`${fmtMoney(payment.totalPaid, subCurrency)} paid`}
            />
            <FlowArrow />
            <FlowNode
              shape="pill"
              bg="#1D4ED8"
              label="Next Payment"
              value={dates.renewalDate ? safeDate(dates.renewalDate, 'MMM dd') : 'N/A'}
              caption={`${fmtMoney(payment.monthlyAmount, subCurrency)}/mo`}
            />
          </Box>
        )}
      </Paper>

      <Grid container spacing={2}>
        {/* Payment summary (not shown elsewhere on the order) */}
        <Grid item xs={12} md={6}>
          <Paper elevation={0} sx={{ p: 2, bgcolor: '#fff', border: '1px solid #E0E7FF', borderRadius: 2, height: '100%' }}>
            <Typography variant="subtitle2" fontWeight="bold" color="#16A34A" gutterBottom>💳 Payment Summary</Typography>
            <Divider sx={{ mb: 1.5 }} />
            <Box sx={{ display: 'flex', gap: 3, mb: payment.discountPercentage > 0 ? 1.5 : 0 }}>
              <Box>
                <Typography variant="h6" color="success.main" fontWeight="bold">
                  {fmtMoney(payment.totalPaid, subCurrency)}
                </Typography>
                <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ textTransform: 'uppercase', letterSpacing: 0.4, fontSize: '0.7rem' }}>Total Paid</Typography>
              </Box>
              <Box>
                <Typography variant="h6" color="warning.main" fontWeight="bold">
                  {fmtMoney(payment.totalRemaining, subCurrency)}
                </Typography>
                <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ textTransform: 'uppercase', letterSpacing: 0.4, fontSize: '0.7rem' }}>Remaining</Typography>
              </Box>
            </Box>
            {payment.discountPercentage > 0 && (
              <Typography variant="body2">
                {payment.discountPercentage}% off — saving {fmtMoney(payment.discountAmount, subCurrency)}/mo
              </Typography>
            )}
          </Paper>
        </Grid>

        {/* Billing cycle */}
        <Grid item xs={12} md={6}>
          <Paper elevation={0} sx={{ p: 2, bgcolor: '#fff', border: '1px solid #E0E7FF', borderRadius: 2, height: '100%' }}>
            <Typography variant="subtitle2" fontWeight="bold" color="#2563EB" gutterBottom>🔄 Billing Cycle</Typography>
            <Divider sx={{ mb: 1.5 }} />
            <Box sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2" fontWeight="bold">Cycle {cycles.current} / {cycles.total}</Typography>
                <Typography variant="body2" color="primary" fontWeight="bold">{cycles.progress}%</Typography>
              </Box>
              <LinearProgress variant="determinate" value={cycles.progress || 0} sx={{ height: 8, borderRadius: 4 }} />
            </Box>
            <Grid container spacing={1.5}>
              {[
                ['Start', dates.startDate], ['End', dates.endDate],
                ['Period Start', dates.currentPeriodStart], ['Next Renewal', dates.renewalDate],
              ].map(([label, val]) => (
                <Grid item xs={6} key={label}>
                  <Typography variant="caption" color="text.secondary" fontWeight={700} display="block" sx={{ textTransform: 'uppercase', letterSpacing: 0.4, fontSize: '0.7rem' }}>{label}</Typography>
                  <Typography variant="body2" fontWeight={700}>{val ? safeDate(val, 'MMM dd, yyyy') : 'N/A'}</Typography>
                </Grid>
              ))}
            </Grid>
          </Paper>
        </Grid>

        {/* Policy */}
        <Grid item xs={12} md={6}>
          <Paper elevation={0} sx={{ p: 2, bgcolor: '#fff', border: '1px solid #E0E7FF', borderRadius: 2, height: '100%' }}>
            <Typography variant="subtitle2" fontWeight="bold" color="#7C3AED" gutterBottom>📋 Policy</Typography>
            <Divider sx={{ mb: 1.5 }} />
            <Grid container spacing={1.5}>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary" fontWeight={700} display="block" sx={{ textTransform: 'uppercase', letterSpacing: 0.4, fontSize: '0.7rem' }}>Type</Typography>
                <Typography variant="body2" fontWeight={700}>{sub.subscriptionType?.displayName || 'N/A'}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary" fontWeight={700} display="block" sx={{ textTransform: 'uppercase', letterSpacing: 0.4, fontSize: '0.7rem' }}>Can Cancel</Typography>
                <Chip
                  label={sub.subscriptionType?.cancellationAllowed ? 'Yes' : 'No'}
                  size="small"
                  color={sub.subscriptionType?.cancellationAllowed ? 'success' : 'error'}
                  sx={{ mt: 0.3 }}
                />
              </Grid>
              {sub.policy && (
                <>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary" fontWeight={700} display="block" sx={{ textTransform: 'uppercase', letterSpacing: 0.4, fontSize: '0.7rem' }}>Cooling-off</Typography>
                    <Typography variant="body2" fontWeight={700}>{sub.policy.coolingOff?.days ?? 0} days</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary" fontWeight={700} display="block" sx={{ textTransform: 'uppercase', letterSpacing: 0.4, fontSize: '0.7rem' }}>Cancellation Window</Typography>
                    <Typography variant="body2" fontWeight={700}>{sub.policy.cancellation?.windowDays ?? 0} days</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary" fontWeight={700} display="block" sx={{ textTransform: 'uppercase', letterSpacing: 0.4, fontSize: '0.7rem' }}>Auto Renew</Typography>
                    <Chip
                      label={sub.policy.renewal?.autoRenewEnabled ? 'Yes' : 'No'}
                      size="small"
                      color={sub.policy.renewal?.autoRenewEnabled ? 'success' : 'default'}
                      sx={{ mt: 0.3 }}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary" fontWeight={700} display="block" sx={{ textTransform: 'uppercase', letterSpacing: 0.4, fontSize: '0.7rem' }}>Pro-rata Refund</Typography>
                    <Chip
                      label={sub.policy.proRataRefundEnabled ? 'Yes' : 'No'}
                      size="small"
                      color={sub.policy.proRataRefundEnabled ? 'info' : 'default'}
                      sx={{ mt: 0.3 }}
                    />
                  </Grid>
                </>
              )}
            </Grid>
          </Paper>
        </Grid>

        {/* Refund info */}
        {isRefunded && (
          <Grid item xs={12} md={6}>
            <Paper elevation={0} sx={{ p: 2, bgcolor: '#fff', borderRadius: 2, height: '100%', border: '2px solid #4caf50' }}>
              <Typography variant="subtitle2" fontWeight="bold" color="success.main" gutterBottom>✅ Refund Issued</Typography>
              <Divider sx={{ mb: 1.5 }} />
              <Grid container spacing={1.5}>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary" fontWeight={700} display="block" sx={{ textTransform: 'uppercase', letterSpacing: 0.4, fontSize: '0.7rem' }}>Amount</Typography>
                  <Typography variant="body1" color="success.main" fontWeight="bold">
                    {fmtMoney(metadata.refundAmount, subCurrency)}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary" fontWeight={700} display="block" sx={{ textTransform: 'uppercase', letterSpacing: 0.4, fontSize: '0.7rem' }}>Status</Typography>
                  <Chip label={metadata.refundStatus || 'succeeded'} color="success" size="small" sx={{ mt: 0.5, display: 'block', width: 'fit-content' }} />
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary" fontWeight={700} display="block" sx={{ textTransform: 'uppercase', letterSpacing: 0.4, fontSize: '0.7rem' }}>Date</Typography>
                  <Typography variant="body2" fontWeight={700}>{safeDate(metadata.refundedAt, 'MMM dd, yyyy')}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary" fontWeight={700} display="block" sx={{ textTransform: 'uppercase', letterSpacing: 0.4, fontSize: '0.7rem' }}>Refund ID</Typography>
                  <Typography variant="body2" fontWeight={700} sx={{ fontFamily: 'monospace', fontSize: '0.7rem', wordBreak: 'break-all' }}>
                    {metadata.refundId}
                  </Typography>
                </Grid>
              </Grid>
            </Paper>
          </Grid>
        )}
      </Grid>

      {/* Refund Dialog */}
      <Dialog open={refundDialog} onClose={() => setRefundDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle fontWeight="bold">Issue Refund & Cancel Subscription</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            ⚠️ This will refund the customer AND cancel the subscription immediately.
          </Alert>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Leave the amount empty to refund the full last payment.
          </Typography>
          <TextField
            label="Refund Amount (optional)" type="number" fullWidth
            value={refundAmount} onChange={(e) => setRefundAmount(e.target.value)}
            placeholder={`Full refund: ${fmtMoney(payment.monthlyAmount, subCurrency)}`}
            sx={{ mb: 2 }} inputProps={{ min: 0, step: '0.01' }}
          />
          <TextField
            select label="Reason" fullWidth
            value={refundReason} onChange={(e) => setRefundReason(e.target.value)}
            sx={{ mb: 2 }}>
            <MenuItem value="requested_by_customer">Requested by Customer</MenuItem>
            <MenuItem value="duplicate">Duplicate Payment</MenuItem>
            <MenuItem value="fraudulent">Fraudulent</MenuItem>
          </TextField>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setRefundDialog(false)} sx={{ color: '#757575' }}>Cancel</Button>
          <Button onClick={handleIssueRefund} variant="contained" sx={{ bgcolor: '#ff9800' }}>
            Issue Refund &amp; Cancel
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

const OrderDetailPage = () => {
  const { orderId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.auth);
  const canUpdate = usePermission('orders', 'update');
  const { userInfo } = location.state || {};
  const authHeader = { headers: { Authorization: `Bearer ${user?.accessToken}` } };

  const [userData] = useState(userInfo || null);
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [orderStatus, setOrderStatus] = useState('');
  const [paymentStatus, setPaymentStatus] = useState('');
  const [savingStatus, setSavingStatus] = useState(false);
  const [downloadingInvoice, setDownloadingInvoice] = useState(false);
  // Elevate (Max PM) sync: { elevateClientId, elevateProjectId, elevateSyncedAt, elevateSyncError }
  const [elevate, setElevate] = useState(null);
  const [sendingToElevate, setSendingToElevate] = useState(false);
  const [elevateDialogOpen, setElevateDialogOpen] = useState(false);
  const [elevateProjectName, setElevateProjectName] = useState('');

  // ── helpers ──────────────────────────────────────────────────────────────────
  const currency = order?.summary?.currency?.toUpperCase() || order?.country?.currencyCode || '';
  const finalTotal = order?.summary?.finalTotal ?? 0;
  const subtotal = order?.summary?.subtotal ?? 0;
  const tax = order?.summary?.tax ?? 0;
  const promoDiscount = order?.summary?.discount ?? 0;

  const createdAt = order?.dates?.createdAt || order?.createdAt;
  const updatedAt = order?.dates?.updatedAt || order?.updatedAt;

  const paymentMethod = order?.payment?.gateway || order?.payment?.paymentMethodType || '-';
  const paymentId = order?.payment?.paymentIntentId || order?.payment?.razorpayPaymentId || '-';
  const transactionId = order?.payment?.paymentIntentId || order?.payment?.razorpayOrderId || '-';
  const orderCountry = order?.country?.name || order?.user?.country?.name || '-';
  const taxRatePercent = order?.summary?.taxRate != null ? `${(order.summary.taxRate * 100).toFixed(0)}%` : '-';
  const subscriptionIds = (order?.subscriptions || []).map((s) => s.razorpaySubscriptionId || s.stripeSubscriptionId).filter(Boolean);

  // ── fetch ────────────────────────────────────────────────────────────────────
  const fetchOrder = async (mounted) => {
    try {
      const { data } = await axios.get(`${ENDPOINT}api/v1/order/getOrder/${orderId}`, {
        headers: { Authorization: `Bearer ${user?.accessToken}` },
      });
      if (!mounted) return;
      if (data.success) {
        const o = data.data.order;           // ← API wraps in { order: {...} }
        setOrder(o);
        setOrderStatus(o.status?.order || '');
        setPaymentStatus(o.status?.payment || '');
      } else {
        Notify.failure('Failed to fetch order');
        navigate('/orders');
      }
    } catch (err) {
      console.error('Error fetching order:', err);
      if (mounted) { Notify.failure('Error fetching order'); navigate('/orders'); }
    }
  };

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    fetchOrder(mounted).finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, [orderId, user, navigate]);

  // ── Elevate (Max PM) ──────────────────────────────────────────────────────
  const elevateUrl = order?.id ? `${ENDPOINT}api/v1/admin-ops/orders/${order.id}/elevate` : null;

  useEffect(() => {
    if (!elevateUrl) return undefined;
    let mounted = true;
    axios
      .get(elevateUrl, authHeader)
      .then(({ data }) => { if (mounted && data.success) setElevate(data.data); })
      .catch(() => {});
    return () => { mounted = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [elevateUrl]);

  // Creates / finds the Max PM client, then the order's project. Safe to repeat:
  // the same email returns the same client, the same order the same project.
  // Opens the modal; the project name there is optional (empty → the client's name)
  const handleSendToElevate = () => {
    setElevateProjectName('');
    setElevateDialogOpen(true);
  };

  // projectName: what the admin typed — sent only when filled in
  const submitToElevate = async () => {
    const projectName = elevateProjectName.trim();
    setElevateDialogOpen(false);
    try {
      setSendingToElevate(true);
      Loading.circle('Sending to Elevate...');
      const { data } = await axios.post(elevateUrl, projectName ? { projectName } : {}, authHeader);
      setElevate(data.data);
      const { client, project } = data.data;
      Notify.success(
        `Client ${client.created ? 'created' : 'already existed'} (#${client.id}) · ` +
          `Project ${project.created ? 'created' : 'already existed'} (#${project.id})`,
        { timeout: 6000 },
      );
    } catch (err) {
      const message = err.response?.data?.message || 'Failed to send to Elevate';
      setElevate((prev) => ({ ...(prev || {}), elevateSyncError: message }));
      Notify.failure(message, { timeout: 6000 });
    } finally {
      Loading.remove();
      setSendingToElevate(false);
    }
  };

  // ── update status ─────────────────────────────────────────────────────────
  const handleUpdateStatus = async () => {
    if (
      orderStatus === (order?.status?.order || '') &&
      paymentStatus === (order?.status?.payment || '')
    ) {
      Notify.info('No changes to update');
      return;
    }
    try {
      setSavingStatus(true);
      Loading.circle('Updating order status...');
      await axios.patch(
        `${ENDPOINT}api/v1/order/updateOrderStatus/${orderId}`,
        { orderStatus, paymentStatus },
        { headers: { Authorization: `Bearer ${user?.accessToken}` } },
      );
      Notify.success('Order status updated');
      await fetchOrder(true);
    } catch (err) {
      console.error('Failed to update order status:', err);
      Notify.failure('Failed to update order status');
    } finally {
      Loading.remove();
      setSavingStatus(false);
    }
  };

  // ── download invoice ──────────────────────────────────────────────────────
  const handleDownloadInvoice = async () => {
    try {
      setDownloadingInvoice(true);
      Loading.circle('Preparing invoice...');
      const response = await axios.get(
        `${ENDPOINT}api/v1/order/admin/${orderId}/invoice`,
        { headers: { Authorization: `Bearer ${user?.accessToken}` }, responseType: 'blob' },
      );
      const blobUrl = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = blobUrl;
      link.setAttribute('download', `Invoice_${order?.orderNumber || orderId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch (err) {
      console.error('Failed to download invoice:', err);
      Notify.failure('Failed to download invoice');
    } finally {
      Loading.remove();
      setDownloadingInvoice(false);
    }
  };

  // ── loading / empty states ────────────────────────────────────────────────
  if (loading) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <Grid container justifyContent="center" alignItems="center" style={{ height: '70vh' }}>
          <Box textAlign="center">
            <CircularProgress size="4rem" />
            <Typography variant="body1" sx={{ mt: 2 }}>Loading order details...</Typography>
          </Box>
        </Grid>
        <Footer />
      </DashboardLayout>
    );
  }

  if (!order) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <Box sx={{ p: 5, textAlign: 'center' }}>
          <Typography variant="h6">No order details available</Typography>
          <Button variant="contained" onClick={() => navigate('/orders')}
            sx={{ mt: 2, color: 'red', border: '1px solid red' }}>
            Back to Orders
          </Button>
        </Box>
        <Footer />
      </DashboardLayout>
    );
  }

  // ── derived display values ────────────────────────────────────────────────
  // user is now nested as order.user.name.first / order.user.name.last
  const userDisplay = {
    firstName: order.user?.name?.first || userData?.firstName || '',
    lastName: order.user?.name?.last || userData?.lastName || '',
    email: order.user?.email || userData?.email || '',
    phoneNumber: order.user?.contact?.phone || '',
    houseNumber: order.user?.contact?.address?.houseNumber || '',
    streetName: order.user?.contact?.address?.street || '',
    city: order.user?.contact?.address?.city || '',
    state: order.user?.contact?.address?.state || '',
    postalCode: order.user?.contact?.address?.postalCode || '',
    country: order.user?.country?.name || '',
  };

  const statusColors = { pending: 'warning', completed: 'success', failed: 'error', on_hold: 'info', in_process: 'info' };
  const paymentColors = { pending: 'warning', partially_paid: 'warning', successful: 'success', succeeded: 'success', failed: 'error' };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox p={3}>

        {/* Header */}
        <Paper elevation={0} sx={{ p: 3, mb: 3, borderRadius: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
              <Typography variant="h4" fontWeight="bold" color="#344767" gutterBottom>
                Order #{order.orderNumber || order.id?.slice(0, 8)}
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                <MDBadge badgeContent={orderStatus} color={statusColors[orderStatus] || 'info'} variant="gradient" size="lg" sx={{ textTransform: 'capitalize' }} />
                <MDBadge badgeContent={paymentStatus} color={paymentColors[paymentStatus] || 'info'} variant="gradient" size="lg" sx={{ textTransform: 'capitalize' }} />
              </Box>
            </Box>
            <Box textAlign="right">
              <Typography variant="h3" fontWeight="bold" color="primary">
                {currency} {parseFloat(finalTotal).toFixed(2)}
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>Total Amount</Typography>
              <MDButton
                variant="outlined"
                color="info"
                size="small"
                startIcon={<DownloadIcon />}
                disabled={downloadingInvoice}
                onClick={handleDownloadInvoice}
              >
                {downloadingInvoice ? 'Preparing...' : 'Download Invoice'}
              </MDButton>

              {/* Elevate (Max PM): client + project for this order */}
              <Box sx={{ mt: 1.5 }}>
                <Tooltip
                  title={
                    order.status?.order !== 'completed'
                      ? 'Only completed orders can be sent'
                      : elevate?.elevateProjectId
                      ? 'Already in Elevate — sending again returns the same client and project'
                      : ''
                  }
                >
                  <span>
                    <MDButton
                      variant="gradient"
                      color={elevate?.elevateProjectId ? 'success' : 'info'}
                      size="small"
                      startIcon={elevate?.elevateProjectId ? <CheckCircleIcon /> : <CloudUploadIcon />}
                      disabled={sendingToElevate || order.status?.order !== 'completed'}
                      onClick={handleSendToElevate}
                    >
                      {sendingToElevate
                        ? 'Sending...'
                        : elevate?.elevateProjectId
                        ? 'Sent to Elevate'
                        : 'Send to Elevate'}
                    </MDButton>
                  </span>
                </Tooltip>
                {elevate?.elevateProjectId && (
                  <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>
                    Client #{elevate.elevateClientId} · Project #{elevate.elevateProjectId}
                    {elevate.elevateSyncedAt && ` · ${format(new Date(elevate.elevateSyncedAt), 'dd MMM yyyy, HH:mm')}`}
                  </Typography>
                )}
                {elevate?.lastEvent && (
                  <Typography
                    variant="caption"
                    display="block"
                    color={elevate.lastEvent.status === 'failed' ? 'error' : 'text.secondary'}
                    sx={{ mt: 0.25, maxWidth: 320, ml: 'auto' }}
                  >
                    Subscription: {elevate.lastEvent.eventType} ·{' '}
                    {elevate.lastEvent.status === 'sent'
                      ? 'sent'
                      : elevate.lastEvent.status === 'failed'
                      ? `failed — ${elevate.lastEvent.lastError || ''}`
                      : `retrying (${elevate.lastEvent.attempts} tries)${elevate.lastEvent.lastError ? ` — ${elevate.lastEvent.lastError}` : ''}`}
                  </Typography>
                )}
                {elevate?.elevateSyncError && (
                  <Typography variant="caption" display="block" color="error" sx={{ mt: 0.5, maxWidth: 320, ml: 'auto' }}>
                    {elevate.elevateSyncError}
                  </Typography>
                )}
              </Box>
            </Box>
          </Box>
        </Paper>

        {/* Quick Stats */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {[
            { icon: <ShoppingCartIcon sx={{ fontSize: 40, color: '#667eea' }} />, value: order.items?.length || 0, label: 'Items' },
            { icon: <PaymentIcon sx={{ fontSize: 40, color: '#4CAF50' }} />, value: `${currency} ${parseFloat(subtotal).toFixed(2)}`, label: 'Subtotal' },
            { icon: <LocalOfferIcon sx={{ fontSize: 40, color: '#FF9800' }} />, value: `${currency} ${parseFloat(promoDiscount).toFixed(2)}`, label: 'Discount' },
            { icon: <ReceiptIcon sx={{ fontSize: 40, color: '#2196F3' }} />, value: `${currency} ${parseFloat(tax).toFixed(2)}`, label: 'Tax' },
          ].map((stat, i) => (
            <Grid item xs={12} md={3} key={i}>
              <Card sx={{ p: 2.5, textAlign: 'center', bgcolor: '#f8f9fa', borderRadius: 3 }}>
                {stat.icon}
                <Typography variant="h4" fontWeight="bold" sx={{ mt: 0.5 }}>{stat.value}</Typography>
                <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ textTransform: 'uppercase', letterSpacing: 0.4, fontSize: '0.7rem' }}>{stat.label}</Typography>
              </Card>
            </Grid>
          ))}
        </Grid>

        <Grid container spacing={3}>
          {/* Customer Info */}
          <Grid item xs={12} md={6}>
            <Card sx={{ p: 3, height: '100%', borderRadius: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <Avatar sx={{ width: 60, height: 60, bgcolor: '#667eea', mr: 2 }}>
                  {userDisplay.firstName?.charAt(0)}{userDisplay.lastName?.charAt(0)}
                </Avatar>
                <Box>
                  <Typography variant="h6" fontWeight="bold">
                    {userDisplay.firstName} {userDisplay.lastName}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">Customer Information</Typography>
                </Box>
              </Box>
              <Divider sx={{ mb: 2 }} />
              <List dense>
                <ListItem sx={{ py: 1 }}>
                  <EmailIcon sx={{ mr: 2, color: '#667eea' }} />
                  <ListItemText primary="Email" secondary={userDisplay.email}
                    primaryTypographyProps={{ variant: 'caption', color: 'text.secondary', fontWeight: 700, sx: { textTransform: 'uppercase', letterSpacing: 0.4, fontSize: '0.7rem' } }}
                    secondaryTypographyProps={{ variant: 'body1', color: 'text.primary', fontWeight: 600 }} />
                </ListItem>
                <ListItem sx={{ py: 1 }}>
                  <PhoneIcon sx={{ mr: 2, color: '#667eea' }} />
                  <ListItemText primary="Phone" secondary={userDisplay.phoneNumber || 'N/A'}
                    primaryTypographyProps={{ variant: 'caption', color: 'text.secondary', fontWeight: 700, sx: { textTransform: 'uppercase', letterSpacing: 0.4, fontSize: '0.7rem' } }}
                    secondaryTypographyProps={{ variant: 'body1', color: 'text.primary', fontWeight: 600 }} />
                </ListItem>
                <ListItem sx={{ py: 1 }}>
                  <LocationIcon sx={{ mr: 2, color: '#667eea' }} />
                  <ListItemText primary="Address"
                    secondary={[userDisplay.houseNumber, userDisplay.streetName, userDisplay.city, userDisplay.state, userDisplay.country, userDisplay.postalCode].filter(Boolean).join(', ') || 'No address provided'}
                    primaryTypographyProps={{ variant: 'caption', color: 'text.secondary', fontWeight: 700, sx: { textTransform: 'uppercase', letterSpacing: 0.4, fontSize: '0.7rem' } }}
                    secondaryTypographyProps={{ variant: 'body1', color: 'text.primary', fontWeight: 600 }} />
                </ListItem>
              </List>
            </Card>
          </Grid>

          {/* Order Management */}
          <Grid item xs={12} md={6}>
            <Card sx={{ p: 3, height: '100%', borderRadius: 3 }}>
              <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                <CalendarIcon sx={{ mr: 1, color: '#667eea' }} /> Order Timeline
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1 }}>
                  <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ textTransform: 'uppercase', letterSpacing: 0.4, fontSize: '0.7rem' }}>Created</Typography>
                  <Typography variant="body1" fontWeight={600}>
                    {createdAt ? format(new Date(createdAt), 'MMM dd, yyyy hh:mm a') : '-'}
                  </Typography>
                </Box>
                <Divider />
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1 }}>
                  <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ textTransform: 'uppercase', letterSpacing: 0.4, fontSize: '0.7rem' }}>Last Updated</Typography>
                  <Typography variant="body1" fontWeight={600}>
                    {updatedAt ? format(new Date(updatedAt), 'MMM dd, yyyy hh:mm a') : '-'}
                  </Typography>
                </Box>
              </Box>
              {canUpdate && (
                <>
                  <Divider sx={{ mb: 3 }} />
                  <Box>
                    <Typography variant="subtitle2" gutterBottom fontWeight="bold">Update Order Status</Typography>
                    <Select value={orderStatus} onChange={(e) => setOrderStatus(e.target.value)}
                      fullWidth size="small" disabled={savingStatus} sx={{ mb: 2 }}>
                      {['pending', 'completed', 'failed', 'on_hold', 'in_process'].map((s) => (
                        <MenuItem key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1).replace('_', ' ')}</MenuItem>
                      ))}
                    </Select>
                    <Typography variant="subtitle2" gutterBottom fontWeight="bold">Update Payment Status</Typography>
                    <Select value={paymentStatus} onChange={(e) => setPaymentStatus(e.target.value)}
                      fullWidth size="small" disabled={savingStatus} sx={{ mb: 2 }}>
                      {['pending', 'successful', 'failed'].map((s) => (
                        <MenuItem key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</MenuItem>
                      ))}
                    </Select>
                    <Button variant="contained" fullWidth onClick={handleUpdateStatus} disabled={savingStatus}
                      sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', '&:hover': { background: 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)' } }}>
                      {savingStatus ? 'Saving...' : 'Update Status'}
                    </Button>
                  </Box>
                </>
              )}
            </Card>
          </Grid>
        </Grid>

        {/* Payment & Billing Information */}
        <Card sx={{ mt: 3, p: 3, borderRadius: 3 }}>
          <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
            <PaymentIcon sx={{ mr: 1, color: '#667eea' }} /> Payment &amp; Billing Information
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <List dense disablePadding>
                <ListItem sx={{ py: 1 }}>
                  <PaymentIcon sx={{ mr: 2, color: '#667eea' }} />
                  <ListItemText primary="Payment Method" secondary={paymentMethod}
                    primaryTypographyProps={{ variant: 'caption', color: 'text.secondary', fontWeight: 700, sx: { textTransform: 'uppercase', letterSpacing: 0.4, fontSize: '0.7rem' } }}
                    secondaryTypographyProps={{ variant: 'body1', color: 'text.primary', fontWeight: 600, sx: { textTransform: 'capitalize' } }} />
                </ListItem>
                <ListItem sx={{ py: 1 }}>
                  <ConfirmationNumberIcon sx={{ mr: 2, color: '#667eea' }} />
                  <ListItemText primary="Payment ID" secondary={paymentId}
                    primaryTypographyProps={{ variant: 'caption', color: 'text.secondary', fontWeight: 700, sx: { textTransform: 'uppercase', letterSpacing: 0.4, fontSize: '0.7rem' } }}
                    secondaryTypographyProps={{ variant: 'body2', color: 'text.primary', fontWeight: 700, sx: { wordBreak: 'break-all' } }} />
                </ListItem>
                <ListItem sx={{ py: 1 }}>
                  <ReceiptIcon sx={{ mr: 2, color: '#667eea' }} />
                  <ListItemText primary="Transaction ID" secondary={transactionId}
                    primaryTypographyProps={{ variant: 'caption', color: 'text.secondary', fontWeight: 700, sx: { textTransform: 'uppercase', letterSpacing: 0.4, fontSize: '0.7rem' } }}
                    secondaryTypographyProps={{ variant: 'body2', color: 'text.primary', fontWeight: 700, sx: { wordBreak: 'break-all' } }} />
                </ListItem>
              </List>
            </Grid>
            <Grid item xs={12} sm={6}>
              <List dense disablePadding>
                <ListItem sx={{ py: 1 }}>
                  <CountryIcon sx={{ mr: 2, color: '#667eea' }} />
                  <ListItemText primary="Country" secondary={orderCountry}
                    primaryTypographyProps={{ variant: 'caption', color: 'text.secondary', fontWeight: 700, sx: { textTransform: 'uppercase', letterSpacing: 0.4, fontSize: '0.7rem' } }}
                    secondaryTypographyProps={{ variant: 'body1', color: 'text.primary', fontWeight: 600 }} />
                </ListItem>
                <ListItem sx={{ py: 1 }}>
                  <LocalOfferIcon sx={{ mr: 2, color: '#667eea' }} />
                  <ListItemText primary="GST / Tax Rate" secondary={taxRatePercent}
                    primaryTypographyProps={{ variant: 'caption', color: 'text.secondary', fontWeight: 700, sx: { textTransform: 'uppercase', letterSpacing: 0.4, fontSize: '0.7rem' } }}
                    secondaryTypographyProps={{ variant: 'body1', color: 'text.primary', fontWeight: 600 }} />
                </ListItem>
                <ListItem sx={{ py: 1 }}>
                  <AutorenewIcon sx={{ mr: 2, color: '#667eea' }} />
                  <ListItemText primary="Subscription ID(s)"
                    secondary={subscriptionIds.length ? subscriptionIds.join(', ') : '-'}
                    primaryTypographyProps={{ variant: 'caption', color: 'text.secondary', fontWeight: 700, sx: { textTransform: 'uppercase', letterSpacing: 0.4, fontSize: '0.7rem' } }}
                    secondaryTypographyProps={{ variant: 'body1', color: 'text.primary', fontWeight: 600 }} />
                </ListItem>
              </List>
            </Grid>
          </Grid>
        </Card>

        {/* Order Items */}
        <MDBox mt={4}>
          <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
            <ShoppingCartIcon sx={{ mr: 1, color: '#667eea' }} /> Order Items
          </Typography>
          {(order.items?.length ?? 0) === 0 && (order.valuePackItems?.length ?? 0) === 0 ? (
            <Alert severity="info">No items in this order.</Alert>
          ) : (
            <Grid container spacing={2}>
              {order.items.map((item) => {
                // New API structure: item.product.name, item.pricing.totalPrice etc.
                const name = item.product?.name || 'Unknown Item';
                const description = item.product?.description || '';
                const tier = item.product?.tier || '';
                const features = item.features || [];
                const addOns = item.addOns || [];
                const totalPrice = item.pricing?.totalPrice ?? item.totalPrice ?? 0;
                const pricePerMonth = item.pricing?.pricePerMonth ?? 0;
                const quantity = item.pricing?.quantity ?? item.quantity ?? 1;

                const hasSubscription = item.billingType === 'recurring';
                const subscriptionId = item.subscriptionDetails?.id || getSubscriptionIdForItem(order, item.id);

                return (
                  <Grid item xs={12} key={item.id}>
                    <Card sx={{
                      p: 3, borderRadius: 3, border: '2px solid #f0f0f0', transition: 'all 0.3s',
                      '&:hover': { borderColor: '#667eea', boxShadow: '0 8px 24px rgba(102,126,234,0.15)' }
                    }}>

                      {/* Item header */}
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 2 }}>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="h6" fontWeight="bold" gutterBottom>{name}</Typography>
                          {description && <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{description}</Typography>}
                          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                            <Chip label={item.itemType} size="small" color="primary" icon={<ShoppingCartIcon />} />
                            <Chip label={item.billingType} size="small"
                              color={item.billingType === 'recurring' ? 'success' : 'default'}
                              icon={item.billingType === 'recurring' ? <AutorenewIcon /> : <CheckCircleIcon />} />
                            {tier && <Chip label={tier} size="small" color="secondary" variant="outlined" />}
                          </Box>
                        </Box>
                        <Box textAlign="right">
                          <Typography variant="h5" fontWeight="bold" color="primary">
                            {currency} {parseFloat(totalPrice).toFixed(2)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ textTransform: 'uppercase', letterSpacing: 0.4, fontSize: '0.7rem' }}>Total Price</Typography>
                        </Box>
                      </Box>

                      <Divider sx={{ my: 2 }} />

                      {/* Pricing grid */}
                      <Grid container spacing={2} sx={{ mb: 2 }}>
                        <Grid item xs={6} sm={3}>
                          <Paper elevation={0} sx={{ p: 2, bgcolor: '#f8f9fa', textAlign: 'center', borderRadius: 2 }}>
                            <Typography variant="caption" color="text.secondary" fontWeight={700} display="block" sx={{ textTransform: 'uppercase', letterSpacing: 0.4, fontSize: '0.7rem' }}>Quantity</Typography>
                            <Typography variant="h6" fontWeight="bold">{quantity}</Typography>
                          </Paper>
                        </Grid>
                        {pricePerMonth > 0 && (
                          <Grid item xs={6} sm={3}>
                            <Paper elevation={0} sx={{ p: 2, bgcolor: '#f8f9fa', textAlign: 'center', borderRadius: 2 }}>
                              <Typography variant="caption" color="text.secondary" fontWeight={700} display="block" sx={{ textTransform: 'uppercase', letterSpacing: 0.4, fontSize: '0.7rem' }}>Monthly Price</Typography>
                              <Typography variant="h6" fontWeight="bold">{currency} {parseFloat(pricePerMonth).toFixed(2)}</Typography>
                            </Paper>
                          </Grid>
                        )}
                        {item.duration?.name && (
                          <Grid item xs={6} sm={3}>
                            <Paper elevation={0} sx={{ p: 2, bgcolor: '#f8f9fa', textAlign: 'center', borderRadius: 2 }}>
                              <Typography variant="caption" color="text.secondary" fontWeight={700} display="block" sx={{ textTransform: 'uppercase', letterSpacing: 0.4, fontSize: '0.7rem' }}>Duration</Typography>
                              <Typography variant="h6" fontWeight="bold">{item.duration.name}</Typography>
                            </Paper>
                          </Grid>
                        )}
                        <Grid item xs={6} sm={3}>
                          <Paper elevation={0} sx={{ p: 2, bgcolor: '#f8f9fa', textAlign: 'center', borderRadius: 2 }}>
                            <Typography variant="caption" color="text.secondary" fontWeight={700} display="block" sx={{ textTransform: 'uppercase', letterSpacing: 0.4, fontSize: '0.7rem' }}>Unit Price</Typography>
                            <Typography variant="h6" fontWeight="bold">
                              {currency} {(parseFloat(totalPrice) / Math.max(quantity, 1)).toFixed(2)}
                            </Typography>
                          </Paper>
                        </Grid>
                      </Grid>

                      {/* Features */}
                      {features.length > 0 && (
                        <>
                          <Divider sx={{ my: 2 }} />
                          <Typography variant="subtitle2" fontWeight="bold" gutterBottom>✨ Included Features</Typography>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                            {features.map((f, i) => (
                              <Chip key={i} size="small" variant="outlined"
                                label={typeof f === 'string' ? f : f.name || 'Feature'}
                                sx={{ borderColor: '#667eea', color: '#667eea' }} />
                            ))}
                          </Box>
                        </>
                      )}

                      {/* Add-ons */}
                      {addOns.length > 0 && (
                        <>
                          <Divider sx={{ my: 2 }} />
                          <Typography variant="subtitle2" fontWeight="bold" gutterBottom>🎁 Selected Add-ons</Typography>
                          <Grid container spacing={1}>
                            {addOns.map((addon, i) => (
                              <Grid item xs={12} sm={6} key={i}>
                                <Paper elevation={0} sx={{ p: 2, bgcolor: '#f5f7fa', borderRadius: 2, border: '1px solid #e0e0e0' }}>
                                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                                    <Box sx={{ flex: 1 }}>
                                      <Typography variant="body2" fontWeight="bold">{addon.name || 'Add-on'}</Typography>
                                      {addon.description && <Typography variant="caption" color="text.secondary">{addon.description}</Typography>}
                                    </Box>
                                    {addon.monthlyPrice && (
                                      <Chip label={`${currency} ${parseFloat(addon.monthlyPrice).toFixed(2)}/mo`} size="small" color="success" />
                                    )}
                                  </Box>
                                </Paper>
                              </Grid>
                            ))}
                          </Grid>
                        </>
                      )}

                      {/* Subscription info — full details merged in, not a separate page */}
                      {hasSubscription && subscriptionId ? (
                        <>
                          <Divider sx={{ my: 2 }} />
                          <SubscriptionPanel subscriptionId={subscriptionId} authHeader={authHeader} />
                        </>
                      ) : hasSubscription && !subscriptionId ? (
                        <>
                          <Divider sx={{ my: 2 }} />
                          <Alert severity="warning" icon={<AutorenewIcon />} sx={{ borderRadius: 2 }}>
                            <Typography variant="body2" fontWeight="bold">⏳ Subscription Pending</Typography>
                            <Typography variant="caption">This is a recurring item. Subscription will be created after payment confirmation.</Typography>
                          </Alert>
                        </>
                      ) : null}
                    </Card>
                  </Grid>
                );
              })}

              {/* Value-pack items — recurring value packs live here, not in order.items */}
              {order.valuePackItems?.map((item) => {
                const name = item.valuePack?.name || 'Unknown Pack';
                const description = item.valuePack?.description || '';
                const tier = item.tier?.name || item.tier?.type || '';
                const totalPrice = item.calculated?.totalPrice ?? item.pricing?.finalTotalPrice ?? 0;
                const pricePerMonth = item.pricing?.finalPricePerMonth ?? 0;
                const quantity = item.quantity ?? 1;
                const durationName = item.tier?.duration?.displayText || item.duration?.name || '';

                const hasSubscription = item.billingType === 'recurring';
                const subscriptionId = hasSubscription ? getSubscriptionIdForItem(order, item.id) : null;

                return (
                  <Grid item xs={12} key={item.id}>
                    <Card sx={{
                      p: 3, borderRadius: 3, border: '2px solid #f0f0f0', transition: 'all 0.3s',
                      '&:hover': { borderColor: '#667eea', boxShadow: '0 8px 24px rgba(102,126,234,0.15)' }
                    }}>

                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 2 }}>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="h6" fontWeight="bold" gutterBottom>{name}</Typography>
                          {description && <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{description}</Typography>}
                          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                            <Chip label="valuepack" size="small" color="primary" icon={<ShoppingCartIcon />} />
                            <Chip label={item.billingType} size="small"
                              color={item.billingType === 'recurring' ? 'success' : 'default'}
                              icon={item.billingType === 'recurring' ? <AutorenewIcon /> : <CheckCircleIcon />} />
                            {tier && <Chip label={tier} size="small" color="secondary" variant="outlined" />}
                          </Box>
                        </Box>
                        <Box textAlign="right">
                          <Typography variant="h5" fontWeight="bold" color="primary">
                            {currency} {parseFloat(totalPrice).toFixed(2)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ textTransform: 'uppercase', letterSpacing: 0.4, fontSize: '0.7rem' }}>Total Price</Typography>
                        </Box>
                      </Box>

                      <Divider sx={{ my: 2 }} />

                      <Grid container spacing={2}>
                        <Grid item xs={6} sm={3}>
                          <Paper elevation={0} sx={{ p: 2, bgcolor: '#f8f9fa', textAlign: 'center', borderRadius: 2 }}>
                            <Typography variant="caption" color="text.secondary" fontWeight={700} display="block" sx={{ textTransform: 'uppercase', letterSpacing: 0.4, fontSize: '0.7rem' }}>Quantity</Typography>
                            <Typography variant="h6" fontWeight="bold">{quantity}</Typography>
                          </Paper>
                        </Grid>
                        {pricePerMonth > 0 && (
                          <Grid item xs={6} sm={3}>
                            <Paper elevation={0} sx={{ p: 2, bgcolor: '#f8f9fa', textAlign: 'center', borderRadius: 2 }}>
                              <Typography variant="caption" color="text.secondary" fontWeight={700} display="block" sx={{ textTransform: 'uppercase', letterSpacing: 0.4, fontSize: '0.7rem' }}>Monthly Price</Typography>
                              <Typography variant="h6" fontWeight="bold">{currency} {parseFloat(pricePerMonth).toFixed(2)}</Typography>
                            </Paper>
                          </Grid>
                        )}
                        {durationName && (
                          <Grid item xs={6} sm={3}>
                            <Paper elevation={0} sx={{ p: 2, bgcolor: '#f8f9fa', textAlign: 'center', borderRadius: 2 }}>
                              <Typography variant="caption" color="text.secondary" fontWeight={700} display="block" sx={{ textTransform: 'uppercase', letterSpacing: 0.4, fontSize: '0.7rem' }}>Duration</Typography>
                              <Typography variant="h6" fontWeight="bold">{durationName}</Typography>
                            </Paper>
                          </Grid>
                        )}
                        <Grid item xs={6} sm={3}>
                          <Paper elevation={0} sx={{ p: 2, bgcolor: '#f8f9fa', textAlign: 'center', borderRadius: 2 }}>
                            <Typography variant="caption" color="text.secondary" fontWeight={700} display="block" sx={{ textTransform: 'uppercase', letterSpacing: 0.4, fontSize: '0.7rem' }}>Unit Price</Typography>
                            <Typography variant="h6" fontWeight="bold">
                              {currency} {(parseFloat(totalPrice) / Math.max(quantity, 1)).toFixed(2)}
                            </Typography>
                          </Paper>
                        </Grid>
                      </Grid>

                      {/* Subscription info */}
                      {hasSubscription && subscriptionId ? (
                        <>
                          <Divider sx={{ my: 2 }} />
                          <SubscriptionPanel subscriptionId={subscriptionId} authHeader={authHeader} />
                        </>
                      ) : hasSubscription && !subscriptionId ? (
                        <>
                          <Divider sx={{ my: 2 }} />
                          <Alert severity="warning" icon={<AutorenewIcon />} sx={{ borderRadius: 2 }}>
                            <Typography variant="body2" fontWeight="bold">⏳ Subscription Pending</Typography>
                            <Typography variant="caption">This is a recurring item. Subscription will be created after payment confirmation.</Typography>
                          </Alert>
                        </>
                      ) : null}
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          )}
        </MDBox>

        {/* Price Summary */}
        <Card sx={{ mt: 4, p: 3, borderRadius: 3, bgcolor: '#f8f9fa' }}>
          <Typography variant="h6" fontWeight="bold" gutterBottom>💰 Price Breakdown</Typography>
          <Divider sx={{ mb: 2 }} />
          <Box sx={{ maxWidth: 500, ml: 'auto' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 1 }}>
              <Typography variant="body1" fontWeight={600} color="text.secondary">Subtotal</Typography>
              <Typography variant="body1" fontWeight={700}>{currency} {parseFloat(subtotal).toFixed(2)}</Typography>
            </Box>
            {parseFloat(promoDiscount) > 0 && (
              <>
                <Divider />
                <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 1 }}>
                  <Typography variant="body1" fontWeight={600} color="success.main">Discount</Typography>
                  <Typography variant="body1" fontWeight={700} color="success.main">
                    - {currency} {parseFloat(promoDiscount).toFixed(2)}
                  </Typography>
                </Box>
              </>
            )}
            <Divider />
            <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 1 }}>
              <Typography variant="body1" fontWeight={600} color="text.secondary">Tax</Typography>
              <Typography variant="body1" fontWeight={700}>{currency} {parseFloat(tax).toFixed(2)}</Typography>
            </Box>
            <Divider sx={{ my: 2 }} />
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="h6" fontWeight="bold">Final Total</Typography>
              <Typography variant="h6" fontWeight="bold" color="primary">{currency} {parseFloat(finalTotal).toFixed(2)}</Typography>
            </Box>
          </Box>
        </Card>

        {/* Back */}
        <Box sx={{ mt: 4, textAlign: 'center' }}>
          <Button variant="outlined" size="large" onClick={() => navigate('/orders')}
            sx={{ minWidth: 200, color: 'red', border: '1px solid red' }}>
            ← Back to Orders
          </Button>
        </Box>

      </MDBox>

      {/* Send to Elevate: optional project name */}
      <Dialog open={elevateDialogOpen} onClose={() => setElevateDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle fontWeight="bold">Send to Elevate</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Creates this customer and the order&apos;s project in Elevate.
          </Typography>
          <TextField
            label="Project Name (optional)"
            fullWidth
            autoFocus
            value={elevateProjectName}
            onChange={(e) => setElevateProjectName(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') submitToElevate(); }}
            inputProps={{ maxLength: 255 }}
            helperText={
              elevate?.elevateProjectId
                ? 'The project already exists in Elevate, so its name will not change.'
                : 'Leave empty to use the client name from the billing address.'
            }
          />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setElevateDialogOpen(false)} sx={{ color: '#757575' }}>Cancel</Button>
          <Button onClick={submitToElevate} variant="contained" sx={{ color: '#fff' }}>
            Send
          </Button>
        </DialogActions>
      </Dialog>

      <Footer />
    </DashboardLayout>
  );
};

export default OrderDetailPage;